import { CometChat } from '@cometchat-pro/chat';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import some from 'lodash/some';
import toString from 'lodash/toString';
import { RcFile } from 'antd/lib/upload';

import { AppDispatch, GetRootState } from 'App';
import MessageNotification from 'components/MessageNotification/MessageNotification';
import {
  activeChatIdSelector,
  activeChatMessagesSelector,
  chatByActiveChatIdSelector,
  chatsListSelector,
} from 'store/selectors/messagesSelectors';
import { userDataSelector } from 'store/selectors/userSelectors';
import { MessagesActionsEnum } from 'store/reducers/messagesReducer/messagesTypes';
import Users from 'api/classes/Users';
import {
  getActiveChatMessagesRequest,
  getChatsListRequest,
  getSingleChatRequest,
  getUserDetailsRequest,
  sendMessageRequest,
} from 'api/requests/messagesRequests';
import history from 'utils/history';
import { immutableSet } from 'utils/immutableSet';
import { mixpanelTrack } from 'utils/mixpanel/mixpanel';
import sendNotification, { ENotificationTypes } from 'utils/sendNotification';
import { ERoutePaths } from 'types/routes/enums';

export const getUnreadCountMessagesAction = () => async(dispatch: AppDispatch) => {
  const unreadList: any = await CometChat.getUnreadMessageCountForAllUsers();

  dispatch({ type: MessagesActionsEnum.GetUnreadCountMessages, payload: { unreadList } });
};

export const setChatStatusAction = (payload: { isConnected: boolean } | { isLoggedInChat: boolean }) =>
  ({ type: MessagesActionsEnum.SetChatStatus, payload });

export const getChatsListAction = () => async(dispatch: AppDispatch) => {
  const { success, data: list } = await getChatsListRequest();

  if (success) {
    dispatch({
      type: MessagesActionsEnum.GetChatsList,
      payload: { list, isCanUpdate: list.length >= 15 },
    });
  }
};

export const getActiveChatMessagesAction = (messageId?: number) =>
  async(dispatch: AppDispatch, getStore: GetRootState) => {
    const activeId = activeChatIdSelector(getStore());

    const { success, data }: any = await getActiveChatMessagesRequest(activeId, messageId);

    if (success) {
      dispatch({
        type: MessagesActionsEnum.GetActiveMessages,
        payload: { messages: data, isCanUpdate: data.length >= 30 },
      });
    }
  };

export const setActiveChatIdAction = (payload: { id: string }) => (dispatch: AppDispatch, getStore: GetRootState) => {
  const activeChatId = activeChatIdSelector(getStore());

  if (activeChatId !== payload.id) {
    dispatch({ type: MessagesActionsEnum.SetActiveChatId, payload });
  }
};

export const sendActiveChatMessageAction = (id: string, message: string | RcFile) => async(
  dispatch: AppDispatch, getStore: GetRootState) => {
  const { conversationId, uid } = chatByActiveChatIdSelector(getStore());
  const messages = activeChatMessagesSelector(getStore());
  const isFirstAnswer = !some(messages, { receiverId: uid });

  const { success, data } = await sendMessageRequest(id, message);

  if (success) {
    dispatch({ type: MessagesActionsEnum.SendMessage, payload: data });
    if (isFirstAnswer) {
      mixpanelTrack('Supplier Chat Message Sent', {
        'Dropshipper User Id': +uid,
        'Conversation Id': conversationId,
      });
    }
  }
};

interface IGetMessageActionPayload {
  message: CometChat.TextMessage | CometChat.MediaMessage,
  newChat?: CometChat.Conversation | undefined
  userId: string,
}

export const getMessageAction = (message: CometChat.TextMessage | CometChat.MediaMessage) =>
  async(dispatch: AppDispatch, getStore: GetRootState) => {
    const { id } = userDataSelector(getStore());
    const chatsList = chatsListSelector(getStore());
    const activeChatId = activeChatIdSelector(getStore());
    const userId = message.getSender().getUid() === id?.toString()
      ? message.getReceiverId()
      : message.getSender().getUid();

    const isChatInList = find(chatsList, ['conversationWith.uid', userId]);
    let payload: IGetMessageActionPayload = { message, userId };

    if (!isChatInList) {
      const { success, data } = await getSingleChatRequest(userId);

      if (success) {
        payload = { message, newChat: data, userId };
      }
    }

    if (userId !== activeChatId) {
      const notificationId = toString(Date.now());

      sendNotification(
        ENotificationTypes.Message,
        <MessageNotification
          id={message.getSender().getUid()}
          name={message.getSender().getName()}
          notificationId={notificationId}
        />,
        notificationId,
      );
    }

    dispatch({ type: MessagesActionsEnum.GetMessage, payload });

    dispatch(getUnreadCountMessagesAction());
  };

export const setIsOnlineUserAction = (user: CometChat.User) => (dispatch: AppDispatch, getStore: GetRootState) => {
  const chatsList = chatsListSelector(getStore());

  // @ts-ignore
  const index = findIndex(chatsList, ['conversationWith.uid', user.getUid()]);

  if (index >= 0) {
    dispatch({
      type: MessagesActionsEnum.SetIsOnlineUser,
      payload: { list: immutableSet(chatsList, index, { ...chatsList[index], conversationWith: user }) },
    });
  }
};

export const setIsUserTypingAction = (payload: { isTyping: boolean }) => ({
  type: MessagesActionsEnum.SetIsTypingUser, payload,
});

export const setMessageReadAction = (payload: CometChat.MessageReceipt) => (dispatch: AppDispatch) => {
  dispatch({ type: MessagesActionsEnum.SetMessageRead, payload });
  dispatch(getUnreadCountMessagesAction());
};

export const sendMessageAction = (id: string, name: string, isRetry = false) =>
  async(dispatch: AppDispatch, getStore: GetRootState) => {
    const chatsList = chatsListSelector(getStore());
    const chatInList = find(chatsList, ['conversationWith.uid', id]);
    let chat;

    if (!chatInList) {
      const { success, data } = await getSingleChatRequest(id);

      if (success && data) {
        chat = data;
      } else {
        const { data: userData } = await getUserDetailsRequest(id);

        if (userData) {
          chat = new CometChat.Conversation(id, 'user', '', userData, 0, []);
        } else {
          // Retried once after activation and the recipient is still missing — stop here:
          // an unbounded retry hammers the APIs forever (AD-5767).
          if (isRetry) {
            sendNotification(ENotificationTypes.Error, 'Something went wrong. Please try again later');

            return;
          }

          // Backend creates the CometChat user (privileged op), so retry once after activation.
          const { success: activateSuccess } = await Users.activateChat({ id });

          if (activateSuccess) {
            await dispatch(sendMessageAction(id, name, true));
          } else {
            sendNotification(ENotificationTypes.Error, 'Something went wrong. Please try again later');
          }

          return;
        }
      }

      dispatch({ type: MessagesActionsEnum.AddChat, payload: { list: [chat], id } });
      dispatch(getUnreadCountMessagesAction());
    } else {
      dispatch(setActiveChatIdAction({ id }));
    }

    history.push(ERoutePaths.Messages);
  };

export const resetMessagesStoreAction = () => ({ type: MessagesActionsEnum.ResetMessagesStore });
